/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Retracked from 'js/app/retracked';

import { breakpoints } from '@coursera/cds-core';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import ReactivateSubscriptionConfirmationModal from 'bundles/subscriptions-common/components/ReactivateSubscriptionConfirmationModal';
import ReactivateSubscriptionModal from 'bundles/subscriptions-common/components/ReactivateSubscriptionModal';
import {
  useEventTracking,
  useUndoCancellationDataForXdp,
} from 'bundles/subscriptions-common/utils/cancellationUndoUtils';

import _t from 'i18n!nls/subscriptions';

const TrackedButton = Retracked.withVisibilityTracking(TrackedCdsButton);

export type Props = {
  isCourseraPlusEligibleProduct?: boolean;
  productItemId?: string;
  s12nName?: string;
  partnerIds?: string[];
};

const styles = {
  buttonContainer: () => css`
    display: inline-block;

    @media (max-width: 1023.95px) {
      width: 100% !important;
      max-width: 100%;
    }
  `,
  button: css`
    width: 100%;
    max-width: 100%;
    padding: var(--cds-spacing-150) var(--cds-spacing-300);

    ${breakpoints.up('md')} {
      min-width: 275px;
      max-width: 275px;
    }
  `,
};

const ReactivateSubscriptionXdpCtaButton = ({
  isCourseraPlusEligibleProduct,
  productItemId,
  s12nName,
  partnerIds,
}: Props) => {
  const [hideCtaButton, setHideCtaButton] = useState(false);
  const [showReactivateSubscriptionModal, setShowReactivateSubscriptionModal] = useState(false);
  const [showReactivateSubscriptionConfirmationModal, setShowReactivateSubscriptionConfirmationModal] = useState(false);

  const {
    showUndoCancellationCTA,
    subscription,
    monthlySubscriptionPrice,
    restorationEligibilityEndsAt,
    nextBillingDate,
    eligibleSubscriptionIds,
    getCancelledStatusCopyOnXdp,
  } = useUndoCancellationDataForXdp({
    isCourseraPlusEligibleProduct,
    productItemId,
    partnerIds,
  });

  const { reactivateEventingV2Props, trackClickReactivateCta, useGetReactivateCtaTrackingRef } = useEventTracking(
    subscription,
    eligibleSubscriptionIds
  );
  const reactivateCtaTrackingRef = useGetReactivateCtaTrackingRef();

  const toggleShowReactivateSubscriptionModal = () => {
    if (!showReactivateSubscriptionModal) {
      trackClickReactivateCta();
    }
    setShowReactivateSubscriptionModal(!showReactivateSubscriptionModal);
  };

  if (!showUndoCancellationCTA || !subscription || !restorationEligibilityEndsAt || hideCtaButton) {
    return null;
  }

  const handleReactivationSuccess = () => {
    setShowReactivateSubscriptionModal(false);
    setShowReactivateSubscriptionConfirmationModal(true);
  };

  const handleReactivationConfirmationClose = () => {
    setHideCtaButton(true);
    setShowReactivateSubscriptionConfirmationModal(false);
  };

  return (
    <div css={styles.buttonContainer}>
      <ReactivateSubscriptionModal
        isOpen={showReactivateSubscriptionModal}
        onReactivationSuccess={handleReactivationSuccess}
        onClose={toggleShowReactivateSubscriptionModal}
        monthlySubscriptionPrice={monthlySubscriptionPrice}
        subscription={subscription}
        nextBillingDate={nextBillingDate}
        eligibleSubscriptionIds={eligibleSubscriptionIds}
      />
      <ReactivateSubscriptionConfirmationModal
        isOpen={showReactivateSubscriptionConfirmationModal}
        onClose={handleReactivationConfirmationClose}
        subscription={subscription}
        s12nName={s12nName}
      />
      <TrackedButton
        trackingName="reactivate_subscription_cta"
        trackingData={reactivateEventingV2Props}
        data={reactivateEventingV2Props}
        data-testid="reactivate-subscription-cta"
        onClick={toggleShowReactivateSubscriptionModal}
        variant="secondary"
        size="medium"
        fullWidth
        css={styles.button}
      >
        <div ref={reactivateCtaTrackingRef}>
          {subscription.isCourseraPlusSubscription
            ? _t('Reactivate Coursera Plus')
            : _t('Reactivate your subscription')}
          <div className="dual-cta-subtext">{getCancelledStatusCopyOnXdp()}</div>
        </div>
      </TrackedButton>
    </div>
  );
};

export default ReactivateSubscriptionXdpCtaButton;
